import React, {
  CSSProperties,
  PropsWithChildren,
  useEffect,
  useRef,
  useState
} from 'react'
import styled from 'styled-components'
import { truncateMiddle } from 'util/truncateText'

// ── Themed elements ────────────────────────────────────────────────────────────

const ScrollingP = styled.p<{ $textAlign: 'left' | 'center' | 'right' }>`
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.typography.fontNunito};
  text-align: ${({ $textAlign }) => $textAlign};
`
const ScrollingH3 = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.typography.fontPlusJakartaSans};
  font-size: 16px;
  font-weight: 700;
  margin: 0;
`

const ScrollingSpan = styled.p<{ $textAlign: 'left' | 'center' | 'right' }>`
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.typography.fontNunito};
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  text-align: ${({ $textAlign }) => $textAlign};
  width: 100%;
`

// ── Types ──────────────────────────────────────────────────────────────────────

export const IdsToAnimate: number[] = []

interface IProps {
  animateFirstTime?: boolean
  containerStyles?: CSSProperties
  elementStyles?: CSSProperties
  tag: 'p' | 'h3' | 'span'
  title?: string
  onClick?: () => void
  id?: any
  role?: any
  tabIndex?: number
  onKeyDown?: (e: any) => void
  isClassName?: boolean
  elementClassName?: string
  animateName?: boolean
  containerClassName?: string
  textAlign?: 'left' | 'center' | 'right'
  middleEllipsis?: {
    start: number
    end: number
  }
}

// ── Component ──────────────────────────────────────────────────────────────────

const ScrollingText = ({
  animateName,
  middleEllipsis,
  containerStyles,
  tag,
  elementStyles,
  id,
  animateFirstTime,
  children,
  isClassName,
  elementClassName = '',
  containerClassName = '',
  textAlign = 'left',
  ...props
}: PropsWithChildren<IProps>) => {
  const [isHovering, setIsHovering] = useState(false)
  const [difference, setDifference] = useState(0)
  const headingRef = useRef<HTMLParagraphElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [animate, setAnimate] = useState(false)

  const time = difference / 80

  useEffect(() => {
    if (children && headingRef.current) {
      /**
       * Middle-ellipsis text expands to its full width while scrolling. Use
       * the stationary container width so expansion cannot erase the overflow
       * distance needed by subsequent hover animations.
       */
      const availableWidth = middleEllipsis
        ? containerRef.current?.clientWidth || headingRef.current.clientWidth
        : headingRef.current.clientWidth
      const diff = middleEllipsis
        ? (measureRef.current?.scrollWidth || headingRef.current.scrollWidth) -
          availableWidth
        : headingRef.current.scrollWidth - headingRef.current.clientWidth
      setDifference(diff)

      const isAlreadyAnimated = !isClassName && IdsToAnimate.includes(id)
      if (diff > 0 && animateFirstTime && !isAlreadyAnimated) {
        !isClassName && IdsToAnimate.push(id)
        setTimeout(() => setAnimate(true), 500)
        setTimeout(() => setAnimate(false), 4000)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, headingRef.current?.scrollWidth, middleEllipsis])

  useEffect(() => {
    if (animateName !== undefined) setIsHovering(animateName)
  }, [animateName])

  /**
   * The resting value can use a middle ellipsis while hover and first-run
   * marquee still measure and render the complete original string.
   */
  const fullText = typeof children === 'string' ? children : ''
  let displayText = children

  if (middleEllipsis && fullText && difference > 0 && !isHovering && !animate) {
    displayText = truncateMiddle(
      fullText,
      middleEllipsis.start,
      middleEllipsis.end
    )
  }

  // ── Shared inline style — handles scroll animation ─────────────────────────

  const scrollStyle: CSSProperties = {
    position: 'relative',
    left: isHovering || animate ? -difference : '0px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: isHovering || animate ? 'unset' : 'ellipsis',
    transition: `left ${time}s linear, width ${time}s linear`,
    width:
      isHovering || animate
        ? middleEllipsis
          ? measureRef.current?.scrollWidth || headingRef.current?.scrollWidth
          : headingRef.current?.scrollWidth
        : (elementStyles?.width ?? '100%')
  }

  const sharedProps = {
    ...props,
    ref: headingRef,
    style: { ...elementStyles, ...scrollStyle },
    className: elementClassName
  }

  // ── Render correct themed element ──────────────────────────────────────────

  if (tag === 'p') {
    return (
      <div
        ref={containerRef}
        className={containerClassName}
        /**
         * Middle-ellipsis text moves during its animation, so its stationary
         * clipping container owns hover to prevent the scroll cancelling when
         * the moving text leaves the pointer.
         */
        onMouseEnter={
          middleEllipsis && animateName === undefined
            ? () => setIsHovering(true)
            : undefined
        }
        onMouseLeave={
          middleEllipsis && animateName === undefined
            ? () => setIsHovering(false)
            : undefined
        }
        style={{
          overflow: 'hidden',
          width: '100%',
          ...containerStyles
        }}
      >
        {middleEllipsis && (
          <span
            ref={measureRef}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              pointerEvents: 'none'
            }}
          >
            {children}
          </span>
        )}
        <ScrollingP
          {...sharedProps}
          $textAlign={textAlign}
          onMouseEnter={
            middleEllipsis
              ? undefined
              : () => animateName === undefined && setIsHovering(true)
          }
          onMouseLeave={
            middleEllipsis
              ? undefined
              : () => animateName === undefined && setIsHovering(false)
          }
        >
          {displayText}
        </ScrollingP>
      </div>
    )
  }

  if (tag === 'span') {
    return (
      <div
        ref={containerRef}
        className={containerClassName}
        style={{
          overflow: 'hidden',
          width: '100%',
          ...containerStyles
        }}
      >
        {middleEllipsis && (
          <span
            ref={measureRef}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              pointerEvents: 'none'
            }}
          >
            {children}
          </span>
        )}
        <ScrollingSpan
          {...sharedProps}
          $textAlign={textAlign}
          onMouseEnter={() => animateName === undefined && setIsHovering(true)}
          onMouseLeave={() => animateName === undefined && setIsHovering(false)}
        >
          {displayText}
        </ScrollingSpan>
      </div>
    )
  }

  if (tag === 'h3') {
    return (
      <div
        ref={containerRef}
        className={containerClassName}
        style={{
          ...(containerStyles ?? { width: '100%' }),
          overflow: 'hidden'
        }}
      >
        {middleEllipsis && (
          <span
            ref={measureRef}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              pointerEvents: 'none'
            }}
          >
            {children}
          </span>
        )}
        <ScrollingH3
          {...sharedProps}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {displayText}
        </ScrollingH3>
      </div>
    )
  }

  return null
}

export default ScrollingText
